import { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import FillWidthCell from '../../../app/components/FillWidthCell';
import StyledTableRow from '../../../app/components/StyledTableRow';
import {
  deleteRestaurantUserAsync,
  fetchRestaurantUserListAsync,
  resendVerificationEmailAsync,
  RestaurantUserListState,
} from './restaurantUserListSlice';
import RestaurantUserForm from './RestaurantUserForm';
import { RestaurantUser } from '../../../app/data/models/restaurantUsers/restaurantUser';
import { Delete, Edit, Email, Warning } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { ConfirmDialog } from '../../../app/components/ConfirmDialog';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import EmptyScreen from '../../../app/components/EmptyScreen';

const RestaurantUserList = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{
    restaurantId: string;
  }>();
  const { restaurantUsers, status, resendStatus } = useAppSelector(
    (state) => state.restaurantUsers
  ) as RestaurantUserListState;
  const [editMode, setEditMode] = useState(false);
  const [selectedRestaurantUser, setSelectedRestaurantUser] = useState<
    RestaurantUser | undefined
  >(undefined);
  const [resendEmailLoading, setResendEmailLoading] = useState<string | null>(
    null
  );
  const [deletingUserEmail, setDeletingUserEmail] = useState<string | null>(
    null
  );
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] =
    useState(false);

  useEffect(() => {
    if (restaurantId) dispatch(fetchRestaurantUserListAsync({ restaurantId }));
  }, [dispatch, restaurantId]);

  if (status.includes('pending')) return <LoadingComponent />;

  const cancelEdit = () => {
    if (selectedRestaurantUser) setSelectedRestaurantUser(undefined);
    setEditMode(false);
  };

  const handleSelectRestaurantUser = (restaurantUser: RestaurantUser) => {
    setSelectedRestaurantUser(restaurantUser);
    setEditMode(true);
  };

  const handleDeleteRestaurantUser = (restaurantUser: RestaurantUser) => {
    setSelectedRestaurantUser(restaurantUser);
    setIsDeleteConfirmationDialogOpen(true);
  };

  const handleResendVerificationEmail = async (email: string) => {
    if (!canResendEmail(email)) {
      toast.warning(t('restaurant-users.resend-email.cooldown-message'));
      return;
    }

    try {
      setResendEmailLoading(email);
      await dispatch(
        resendVerificationEmailAsync({ restaurantId, email })
      ).unwrap();
      // Success toast is now handled by the error handler middleware
    } catch (error) {
      // Errors are already handled by the error handler middleware
      console.error('Failed to resend verification email:', error);
    } finally {
      setResendEmailLoading(null);
    }
  };

  const canResendEmail = (email: string) => {
    const userStatus = resendStatus[email];
    if (!userStatus?.lastResentAt) return true;

    const oneHourInMs = 60 * 60 * 1000;
    const now = Date.now();
    return now - userStatus.lastResentAt >= oneHourInMs;
  };

  const isUserUnverified = (user: RestaurantUser) => {
    // Only show as unverified if emailConfirmed is explicitly false
    // If emailConfirmed is true, treat as verified
    return user.emailConfirmed === false;
  };

  const handleDeleteConfirmation = async (email: string) => {
    setIsDeleteConfirmationDialogOpen(false);
    if (restaurantId) {
      setDeletingUserEmail(email);
      try {
        await dispatch(deleteRestaurantUserAsync({ restaurantId, email }));
      } finally {
        setDeletingUserEmail(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmationDialogOpen(false);
  };

  if (editMode)
    return (
      <RestaurantUserForm
        restaurantUser={selectedRestaurantUser}
        restaurantId={restaurantId}
        cancelEdit={cancelEdit}
      />
    );

  if (!restaurantUsers || restaurantUsers.length === 0)
    return (
      <EmptyScreen
        title={t('restaurant-users.list.title')}
        emptyText={t('restaurant-users.list.empty')}
        onPrimaryClick={() => setEditMode(true)}
      />
    );

  return (
    <>
      <ConfirmDialog
        open={isDeleteConfirmationDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={() =>
          selectedRestaurantUser &&
          handleDeleteConfirmation(selectedRestaurantUser.email)
        }
        title={t('restaurant-users.delete.title')}
        message={t('restaurant-users.delete.message')}
      />
      <ListTitleBox
        title={t('restaurant-users.list.title')}
        onPrimaryClick={() => setEditMode(true)}
      />
      <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
        <Table aria-label={t('restaurant-users.list.title')}>
          <TableHead>
            <TableRow>
              <TableCell width="50px" align="left">
                #
              </TableCell>
              <FillWidthCell align="left">
                {t('restaurant-users.list.header.name')}
              </FillWidthCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {restaurantUsers.map((restaurantUser, index) => (
              <StyledTableRow
                key={restaurantUser.email}
                onClick={() => {}}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                }}
              >
                <TableCell width="50px" align="left" component="th" scope="row">
                  {index + 1}
                </TableCell>

                <TableCell
                  align="left"
                  sx={{
                    flexDirection: 'column',
                    flexGrow: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">{`${restaurantUser.firstName} ${restaurantUser.lastName}`}</Typography>
                    {isUserUnverified(restaurantUser) && (
                      <Warning
                        color="warning"
                        fontSize="small"
                        titleAccess="Email not verified"
                      />
                    )}
                  </Box>
                  <Typography variant="body2">
                    {restaurantUser.email}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box
                    display="flex"
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <IconButton
                      sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectRestaurantUser(restaurantUser);
                      }}
                      size="small"
                      title={t('common.edit')}
                    >
                      <Edit />
                    </IconButton>
                    {isUserUnverified(restaurantUser) && (
                      <LoadingButton
                        sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                        loading={resendEmailLoading === restaurantUser.email}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResendVerificationEmail(restaurantUser.email);
                        }}
                        size="small"
                        disabled={!canResendEmail(restaurantUser.email)}
                        title={
                          canResendEmail(restaurantUser.email)
                            ? t('restaurant-users.resend-email.button-tooltip')
                            : t(
                                'restaurant-users.resend-email.cooldown-message'
                              )
                        }
                      >
                        <Email />
                      </LoadingButton>
                    )}
                    <LoadingButton
                      sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                      loading={deletingUserEmail === restaurantUser.email}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRestaurantUser(restaurantUser);
                      }}
                      color="error"
                      size="small"
                      title={t('common.delete')}
                    >
                      <Delete />
                    </LoadingButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default RestaurantUserList;
